/**
 * Shared formatting helpers (currency, numbers, dates, quantities)
 */

// Estimate ranges shown to users are ±12%
export const ESTIMATE_VARIANCE = 0.12;

/**
 * Format an amount in Malaysian Ringgit, e.g. "RM 185,000"
 * @param {number} amount
 * @param {{ compact?: boolean }} options - compact gives "RM 1.2M" / "RM 185k"
 */
export const formatCurrency = (amount, { compact = false } = {}) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return 'RM –';
  if (compact && Math.abs(value) >= 1000000) {
    return `RM ${(value / 1000000).toFixed(2).replace(/\.?0+$/, '')}M`;
  }
  if (compact && Math.abs(value) >= 10000) {
    return `RM ${Math.round(value / 1000)}k`;
  }
  return `RM ${new Intl.NumberFormat('en-MY', { maximumFractionDigits: 0 }).format(Math.round(value))}`;
};

/**
 * Format a number with thousands separators, keeping up to `maxDecimals` decimals
 */
export const formatNumber = (num, maxDecimals = 2) => {
  const value = Number(num);
  if (!Number.isFinite(value)) return '0';
  return new Intl.NumberFormat('en-MY', { maximumFractionDigits: maxDecimals }).format(value);
};

/**
 * Format a date for display in the current language
 */
export const formatDate = (dateString, language = 'en') => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(language === 'my' ? 'ms-MY' : 'en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

// Units measured continuously (volumes, tons, lengths, land area) keep two decimals;
// everything else (bags, pieces, sheets, litres, kg, sqft) is counted in whole units.
const CONTINUOUS_UNIT = /m³|m3|ton|tan|cuft|kaki padu|acre|ekar|^m$|^ft$/i;

/**
 * Round a material quantity up, sensibly for its unit
 * - continuous units (m³, tons): up to 2 decimals, e.g. 1.234 m³ → 1.24 m³
 * - countable units (bags, pcs, litres…): whole numbers, e.g. 12.1 bags → 13 bags
 */
export const roundQuantity = (value, unit = '') => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return 0;
  if (CONTINUOUS_UNIT.test(unit)) return Math.ceil(num * 100 - 1e-9) / 100;
  return Math.ceil(num - 1e-9);
};

/**
 * Build a min / avg / max range around a quantity (±12% by default)
 */
export const quantityRange = (value, unit = '', variance = ESTIMATE_VARIANCE) => ({
  min: roundQuantity(value * (1 - variance), unit),
  avg: roundQuantity(value, unit),
  max: roundQuantity(value * (1 + variance), unit),
});
